import React, { useEffect } from "react";
import styles from "./FilterDrawer.module.scss";
import { Drawer } from "antd";
import { filterConsts, filterFieldConstants } from "./constants";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../enums/ButtonAttributesEnums";
import TNGoogleAddressSuggestions from "../../../reusable-components/TNGoogleSearch";
import InputField from "../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import { set, useFormContext } from "react-hook-form";
import {
  CustomerDatabaseFilterValuesType,
  CustomerFilterParams,
} from "../../../Types/customerDatabaseTypes";
import { useCxDatabaseContext } from "../../../context/CustomerDatabaseFilterContext";

interface FilterDrawerProps {
  visible: boolean;
  handleClose: () => void;
  filterValues: CustomerFilterParams;
  handleSetFilters: (filterValues: CustomerDatabaseFilterValuesType) => void;
  handleClearAllFilter: () => void;
}

const FilterDrawer = ({
  visible,
  handleClose,
  filterValues,
  handleSetFilters,
  handleClearAllFilter,
}: FilterDrawerProps) => {
  const { TITLE, WIDTH } = filterConsts;
  const {
    register,
    reset,
    setValue,
    getValues,
    watch,
    handleSubmit,
    clearErrors,
  } = useFormContext();

  const handleSubmitAction = () => {
    handleSetFilters({
      latitude: getValues(filterFieldConstants?.ADDRESS_DATA_FIELD)?.lat,
      longitude: getValues(filterFieldConstants?.ADDRESS_DATA_FIELD)?.lng,
      address: getValues(filterFieldConstants?.ADDRESS_NAME),
      radius: getValues(filterFieldConstants?.RADIUS_NAME),
    });
    handleClose();
  };

  const onCloseAction = () => {
    handleClose();
    clearErrors();
  };

  useEffect(() => {
    register(filterFieldConstants.ADDRESS_DATA_FIELD);
  }, []);

  useEffect(() => {
    if (!visible) return;
    reset({
      [filterFieldConstants.ADDRESS_NAME]: filterValues?.address ?? null,
      [filterFieldConstants.ADDRESS_DATA_FIELD]: {
        lat: filterValues?.latitude ?? null,
        lng: filterValues?.longitude ?? null,
      },
      [filterFieldConstants.RADIUS_NAME]: filterValues?.radius ?? null,
    });
  }, [visible, filterValues]);
  
  const DrawerHeader = <span className={styles.drawerTitle}>{TITLE}</span>;

  const DrawerFooter = (
    <div className={styles.filterButtons}>
      <div>
        <TNButton
          shape={ButtonShape.ROUND}
          size={ButtonSize.LARGE}
          text={CommonButtonTexts.CLEAR_FILTERS}
          ghost
          danger
          onClick={() => {
            handleClearAllFilter();
            onCloseAction();
          }}
        />
      </div>
      <div className={styles.rightSide}>
        <TNButton
          size={ButtonSize.MEDIUM}
          shape={ButtonShape.DEFAULT}
          type={ButtonType.DEFAULT}
          text={CommonButtonTexts.CANCEL}
          onClick={() => {
            onCloseAction();
          }}
        />
        <TNButton
          size={ButtonSize.MEDIUM}
          shape={ButtonShape.DEFAULT}
          type={ButtonType.PRIMARY}
          text={CommonButtonTexts.APPLY}
          onClick={() => {
            handleSubmit(handleSubmitAction)();
          }}
        />
      </div>
    </div>
  );

  return (
    <Drawer
      visible={visible}
      onClose={onCloseAction}
      maskClosable={false}
      width={WIDTH}
      destroyOnClose
      className={styles.customerDatabaseFilter}
      title={DrawerHeader}
      footer={DrawerFooter}
    >
      <div className={styles.drawerContent}>
        <div className={styles.filterFields}>
          <TNGoogleAddressSuggestions
            label={filterFieldConstants.ADDRESS_LABEL}
            name={filterFieldConstants.ADDRESS_NAME}
            placeholder={filterFieldConstants.ADDRESS_PLACEHOLDER}
            requiredField={false}
            isFilterField={true}
          />
          <InputField
            type={InputfieldTypes.NUMBER}
            label={filterFieldConstants.RADIUS_LABEL}
            name={filterFieldConstants.RADIUS_NAME}
            addonLater={filterFieldConstants.RADIUS_ADDON}
            numberPrecision={2}
            value={watch(filterFieldConstants?.RADIUS_NAME)}
            handleNumberChange={(value) => {
              setValue(filterFieldConstants?.RADIUS_NAME, value);
            }}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
